import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  CalendarIcon,
  UserIcon,
  CogIcon,
  CloudIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

export const DocumentationPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const documentCategories = [
    { id: 'all', name: 'Todos', count: 0 },
    { id: 'conformance', name: 'Conformance Statements', count: 0 },
    { id: 'releases', name: 'Release Notes', count: 0 },
    { id: 'guides', name: 'Guías de Usuario', count: 0 },
    { id: 'technical', name: 'Documentación Técnica', count: 0 },
    { id: 'api', name: 'API Documentation', count: 0 }
  ];

  const documents = [
    // DICOM Conformance Statements
    {
      id: 1,
      title: 'DICOM Conformance Statement - DICOMFiles v2.1.0',
      description: 'Declaración completa de conformidad DICOM para DICOMFiles. Incluye soporte para Worklist Query/Retrieve (C-FIND, C-MOVE), Storage Commitment (N-ACTION), y todos los servicios de procesamiento de documentos DICOM encapsulados.',
      category: 'conformance',
      product: 'DICOMFiles',
      version: '2.1.0',
      date: '2024-01-15',
      size: '2.4 MB',
      format: 'PDF',
      downloads: 1247,
      icon: DocumentTextIcon,
      color: 'blue',
      tags: ['DICOM', 'Conformance', 'PACS', 'Worklist', 'Storage Commitment']
    },
    {
      id: 2,
      title: 'DICOM Conformance Statement - DICOMCapture v1.8.0',
      description: 'Declaración de conformidad DICOM para DICOMCapture. Detalla capacidades de captura desde equipos médicos, procesamiento de imágenes DICOM, y envío automático a servidores PACS con validación de calidad.',
      category: 'conformance',
      product: 'DICOMCapture',
      version: '1.8.0',
      date: '2024-01-10',
      size: '1.8 MB',
      format: 'PDF',
      downloads: 892,
      icon: DocumentTextIcon,
      color: 'green',
      tags: ['DICOM', 'Conformance', 'Capture', 'Imaging', 'PACS']
    },
    
    // Release Notes
    {
      id: 3,
      title: 'Release Notes - DICOMFiles v2.1.0',
      description: 'Nuevas características: Automatización mejorada de PDFs con OCR avanzado, integración con sistemas PACS de terceros, monitoreo en tiempo real de procesos, y sistema de notificaciones por email. Correcciones de bugs críticos en el procesamiento de documentos.',
      category: 'releases',
      product: 'DICOMFiles',
      version: '2.1.0',
      date: '2024-01-15',
      size: '456 KB',
      format: 'PDF',
      downloads: 2156,
      icon: CalendarIcon,
      color: 'blue',
      tags: ['Release', 'Features', 'OCR', 'PACS Integration', 'Monitoring']
    },
    {
      id: 4,
      title: 'Release Notes - DICOMCapture v1.8.0',
      description: 'Mejoras significativas en rendimiento: Reducción del 40% en tiempo de procesamiento, nueva interfaz de usuario optimizada, soporte para más formatos de equipos médicos, y sistema de validación automática de calidad mejorado.',
      category: 'releases',
      product: 'DICOMCapture',
      version: '1.8.0',
      date: '2024-01-10',
      size: '389 KB',
      format: 'PDF',
      downloads: 1834,
      icon: CalendarIcon,
      color: 'green',
      tags: ['Release', 'Performance', 'UI/UX', 'Equipment Support', 'Quality Validation']
    },
    {
      id: 5,
      title: 'Release Notes - DICOMFiles v2.0.5',
      description: 'Actualización de seguridad y estabilidad. Correcciones en el manejo de archivos grandes, mejoras en la gestión de memoria, y optimizaciones en el procesamiento de lotes de documentos.',
      category: 'releases',
      product: 'DICOMFiles',
      version: '2.0.5',
      date: '2023-12-20',
      size: '312 KB',
      format: 'PDF',
      downloads: 1892,
      icon: CalendarIcon,
      color: 'blue',
      tags: ['Security', 'Stability', 'Memory Management', 'Batch Processing']
    },
    
    // User Guides
    {
      id: 6,
      title: 'Guía de Usuario - DICOMFiles - Configuración Inicial',
      description: 'Guía completa paso a paso para la instalación y configuración inicial de DICOMFiles. Incluye configuración de carpetas de monitoreo, conexión a servidores PACS, configuración de reglas de procesamiento, y primeros pasos con el sistema.',
      category: 'guides',
      product: 'DICOMFiles',
      version: '2.1.0',
      date: '2024-01-12',
      size: '3.2 MB',
      format: 'PDF',
      downloads: 3421,
      icon: UserIcon,
      color: 'blue',
      tags: ['Setup', 'Installation', 'Configuration', 'Getting Started', 'PACS Setup']
    },
    {
      id: 7,
      title: 'Guía de Usuario - DICOMCapture - Operación Avanzada',
      description: 'Manual completo para operaciones avanzadas de DICOMCapture. Configuración de equipos médicos, flujos de trabajo personalizados, validación de calidad, integración con sistemas hospitalarios, y resolución de problemas comunes.',
      category: 'guides',
      product: 'DICOMCapture',
      version: '1.8.0',
      date: '2024-01-08',
      size: '2.7 MB',
      format: 'PDF',
      downloads: 2789,
      icon: UserIcon,
      color: 'green',
      tags: ['Advanced', 'Workflow', 'Equipment', 'Configuration', 'Troubleshooting']
    },
    {
      id: 8,
      title: 'Guía de Usuario - DICOMFiles - Procesamiento de PDFs',
      description: 'Guía especializada para el procesamiento de documentos PDF con DICOMFiles. Configuración de OCR, reglas de matching de pacientes, conversión a DICOM encapsulado, y optimización de parámetros de procesamiento.',
      category: 'guides',
      product: 'DICOMFiles',
      version: '2.1.0',
      date: '2024-01-05',
      size: '2.1 MB',
      format: 'PDF',
      downloads: 2987,
      icon: UserIcon,
      color: 'blue',
      tags: ['PDF Processing', 'OCR', 'Patient Matching', 'DICOM Encapsulation']
    },
    {
      id: 9,
      title: 'Guía de Usuario - DICOMCapture - Configuración de Equipos',
      description: 'Manual detallado para la configuración de diferentes tipos de equipos médicos con DICOMCapture. Incluye configuraciones específicas para equipos de radiología, tomografía, resonancia magnética, y otros dispositivos DICOM.',
      category: 'guides',
      product: 'DICOMCapture',
      version: '1.8.0',
      date: '2024-01-03',
      size: '2.9 MB',
      format: 'PDF',
      downloads: 2156,
      icon: UserIcon,
      color: 'green',
      tags: ['Equipment Setup', 'Radiology', 'CT', 'MRI', 'DICOM Devices']
    },
    
    // Technical Documentation
    {
      id: 10,
      title: 'Documentación Técnica - Arquitectura del Sistema',
      description: 'Documentación técnica completa sobre la arquitectura de DICOMFiles y DICOMCapture. Componentes del sistema, flujos de datos, protocolos de comunicación, bases de datos, y diseño de la infraestructura.',
      category: 'technical',
      product: 'Ambos',
      version: '2.1.0',
      date: '2024-01-05',
      size: '4.1 MB',
      format: 'PDF',
      downloads: 1567,
      icon: CogIcon,
      color: 'gray',
      tags: ['Architecture', 'System Design', 'Components', 'Data Flow', 'Infrastructure']
    },
    {
      id: 11,
      title: 'Documentación Técnica - Protocolos DICOM',
      description: 'Especificación técnica detallada de los protocolos DICOM implementados. Servicios soportados, objetos de información, transfer syntaxes, y conformidad con estándares DICOM 2023b.',
      category: 'technical',
      product: 'Ambos',
      version: '2.1.0',
      date: '2024-01-02',
      size: '3.7 MB',
      format: 'PDF',
      downloads: 1234,
      icon: CogIcon,
      color: 'gray',
      tags: ['DICOM Protocols', 'Services', 'Information Objects', 'Transfer Syntaxes']
    },
    {
      id: 12,
      title: 'Documentación Técnica - Base de Datos y Almacenamiento',
      description: 'Especificaciones técnicas de la base de datos, esquemas de datos, índices, y estrategias de almacenamiento. Incluye guías de optimización y mantenimiento del sistema de almacenamiento.',
      category: 'technical',
      product: 'Ambos',
      version: '2.1.0',
      date: '2023-12-28',
      size: '2.8 MB',
      format: 'PDF',
      downloads: 987,
      icon: CogIcon,
      color: 'gray',
      tags: ['Database', 'Storage', 'Schema', 'Optimization', 'Maintenance']
    },
    
    // API Documentation
    {
      id: 13,
      title: 'API Documentation - REST Endpoints',
      description: 'Documentación completa de la API REST para integración con sistemas externos. Endpoints para monitoreo, configuración, estadísticas, y automatización de procesos. Incluye ejemplos de código y casos de uso.',
      category: 'api',
      product: 'Ambos',
      version: '2.1.0',
      date: '2024-01-03',
      size: '1.9 MB',
      format: 'PDF',
      downloads: 2341,
      icon: CloudIcon,
      color: 'purple',
      tags: ['API', 'REST', 'Integration', 'Automation', 'Monitoring']
    },
    {
      id: 14,
      title: 'API Documentation - Webhooks y Eventos',
      description: 'Documentación de webhooks y sistema de eventos para integración en tiempo real. Configuración de notificaciones, tipos de eventos, formato de payloads, y ejemplos de implementación.',
      category: 'api',
      product: 'Ambos',
      version: '2.1.0',
      date: '2023-12-30',
      size: '1.4 MB',
      format: 'PDF',
      downloads: 1456,
      icon: CloudIcon,
      color: 'purple',
      tags: ['Webhooks', 'Events', 'Real-time', 'Notifications', 'Integration']
    },
    
    // Installation and Deployment
    {
      id: 15,
      title: 'Guía de Instalación - DICOMFiles en Windows Server',
      description: 'Guía detallada para la instalación de DICOMFiles en entornos Windows Server. Requisitos del sistema, configuración de servicios, permisos, firewall, y optimización de rendimiento.',
      category: 'technical',
      product: 'DICOMFiles',
      version: '2.1.0',
      date: '2024-01-01',
      size: '2.3 MB',
      format: 'PDF',
      downloads: 1876,
      icon: CogIcon,
      color: 'blue',
      tags: ['Installation', 'Windows Server', 'Services', 'Firewall', 'Performance']
    },
    {
      id: 16,
      title: 'Guía de Instalación - DICOMCapture en Linux',
      description: 'Manual de instalación de DICOMCapture en distribuciones Linux. Configuración de dependencias, servicios systemd, permisos de usuario, y optimización para entornos de producción.',
      category: 'technical',
      product: 'DICOMCapture',
      version: '1.8.0',
      date: '2023-12-25',
      size: '2.0 MB',
      format: 'PDF',
      downloads: 1654,
      icon: CogIcon,
      color: 'green',
      tags: ['Installation', 'Linux', 'Systemd', 'Dependencies', 'Production']
    },
    
    // Troubleshooting and Support
    {
      id: 17,
      title: 'Guía de Resolución de Problemas - DICOMFiles',
      description: 'Manual completo de resolución de problemas para DICOMFiles. Problemas comunes, códigos de error, logs del sistema, y procedimientos de diagnóstico y recuperación.',
      category: 'guides',
      product: 'DICOMFiles',
      version: '2.1.0',
      date: '2023-12-22',
      size: '1.8 MB',
      format: 'PDF',
      downloads: 2234,
      icon: UserIcon,
      color: 'blue',
      tags: ['Troubleshooting', 'Error Codes', 'Logs', 'Diagnostics', 'Recovery']
    },
    {
      id: 18,
      title: 'Guía de Resolución de Problemas - DICOMCapture',
      description: 'Guía de resolución de problemas específica para DICOMCapture. Problemas de conectividad con equipos, errores de captura, validación de calidad, y procedimientos de mantenimiento.',
      category: 'guides',
      product: 'DICOMCapture',
      version: '1.8.0',
      date: '2023-12-18',
      size: '1.6 MB',
      format: 'PDF',
      downloads: 1987,
      icon: UserIcon,
      color: 'green',
      tags: ['Troubleshooting', 'Connectivity', 'Capture Errors', 'Quality Validation', 'Maintenance']
    }
  ];

  // Calcular conteos por categoría
  documentCategories.forEach(category => {
    if (category.id === 'all') {
      category.count = documents.length;
    } else {
      category.count = documents.filter(doc => doc.category === category.id).length;
    }
  });

  const filteredDocuments = selectedCategory === 'all' 
    ? documents 
    : documents.filter(doc => doc.category === selectedCategory);

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[color] || colors.gray;
  };

  const getProductBadge = (product) => {
    if (product === 'DICOMFiles') {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else if (product === 'DICOMCapture') {
      return 'bg-green-100 text-green-800 border-green-200';
    } else {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Documentación Técnica
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Accede a toda la documentación técnica, guías de usuario, declaraciones de conformidad DICOM 
              y notas de lanzamiento de nuestros productos.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Categorías
              </h3>
              <nav className="space-y-2">
                {documentCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{category.name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        selectedCategory === category.id
                          ? 'bg-blue-200 text-blue-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {category.count}
                      </span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">

            {/* Documents Grid */}
            <div className="space-y-6">
              {filteredDocuments.map((document) => (
                <div key={document.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`p-3 rounded-lg ${getColorClasses(document.color)}`}>
                        <document.icon className="h-6 w-6" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {document.title}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getProductBadge(document.product)}`}>
                            {document.product}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 mb-4 line-clamp-2">
                          {document.description}
                        </p>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {new Date(document.date).toLocaleDateString('es-ES')}
                          </div>
                          <div className="flex items-center">
                            <DocumentTextIcon className="h-4 w-4 mr-1" />
                            {document.format} • {document.size}
                          </div>
                          <div className="flex items-center">
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                            {document.downloads.toLocaleString()} descargas
                          </div>
                          <div className="flex items-center">
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                              v{document.version}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {document.tags.map((tag, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
                        <EyeIcon className="h-4 w-4 mr-2" />
                        Ver
                      </button>
                      <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Descargar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredDocuments.length === 0 && (
              <div className="text-center py-12">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay documentos</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No se encontraron documentos en esta categoría.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
